"use client";

import { useId, useMemo, useState } from "react";
import { MapPin, Search } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
interface CityCommunity {
  name: string;
  description: string;
}
const PG_COUNTY_COMMUNITIES: CityCommunity[] = [{
  name: "Bowie",
  description: "Prince George's largest municipality with established neighborhoods and active retail corridors."
}, {
  name: "Laurel",
  description: "Historic Patuxent riverfront community bordering Howard, Anne Arundel, and Montgomery counties."
}, {
  name: "College Park",
  description: "Vibrant university community anchored by the University of Maryland and Route 1 corridor."
}, {
  name: "Hyattsville",
  description: "Historic Gateway Arts District hub featuring vintage architectural residences and retail centers."
}, {
  name: "Upper Marlboro",
  description: "The historic county seat surrounded by equestrian properties, civic centers, and rural estates."
}, {
  name: "Greenbelt",
  description: "Pioneering planned garden city centered around Greenbelt Lake and NASA Goddard facilities."
}, {
  name: "Largo",
  description: "Dynamic regional hub home to premier medical campuses and the Blue Line transit corridor."
}, {
  name: "Landover",
  description: "Established residential enclave near central county commercial complexes and parklands."
}, {
  name: "Suitland",
  description: "Gateway community near Washington, D.C., housing key federal government agency centers."
}, {
  name: "Clinton",
  description: "Growing southern county residential center located minutes from Joint Base Andrews."
}];
export default function PrinceGeorgesCountyCities() {
  const searchInputId = useId();
  const [searchQuery, setSearchQuery] = useState("");
  const filteredCommunities = useMemo(() => {
    const trimmed = searchQuery.trim().toLowerCase();
    if (!trimmed) return PG_COUNTY_COMMUNITIES;
    return PG_COUNTY_COMMUNITIES.filter(item => item.name.toLowerCase().includes(trimmed));
  }, [searchQuery]);
  return <section id="princegeorgescountycities" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-10 md:mb-12">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-medium text-xs">
            Service Coverage
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
            Cities &amp; Communities We Serve in Prince George&apos;s County
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default">
            Our local dispatch crews respond 24/7 across municipalities and unincorporated neighborhoods throughout Prince George&apos;s County.
          </p>

          {/* Search Bar */}
          <div className="relative w-full max-w-md mt-8">
            <label htmlFor={searchInputId} className="sr-only">
              Search your city or community
            </label>
            <div className="relative flex items-center">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
              <Input id={searchInputId} type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search your city..." className="pl-10 pr-4 h-11 bg-background text-foreground border-input rounded-lg shadow-xs focus-visible:ring-2 focus-visible:ring-primary text-sm" />
            </div>
          </div>
        </div>

        {/* Communities Grid */}
        {filteredCommunities.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
            {filteredCommunities.map((community, i) => <Card key={community.name} data-index={i} className="bg-card text-card-foreground border-border rounded-lg shadow-xs hover:shadow-md transition-shadow duration-200 select-none cursor-default flex flex-col justify-between">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="p-1 rounded-md bg-muted text-primary inline-flex">
                      <MapPin className="size-3.5" />
                    </span>
                    <CardTitle className="text-lg font-semibold tracking-tight text-foreground font-default">
                      {community.name}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed font-default">
                    {community.description}
                  </p>
                </CardContent>
              </Card>)}
          </div> : <div className="py-12 text-center border border-dashed border-border rounded-lg max-w-lg mx-auto">
            <p className="text-muted-foreground text-sm font-default">
              No municipalities matched &ldquo;{searchQuery}&rdquo;.
            </p>
            <Button variant="outline" size="sm" onClick={() => setSearchQuery("")} className="mt-3">
              Reset search
            </Button>
          </div>}

        {/* Closing Call-To-Action Block */}
        <div className="mt-14 md:mt-18 pt-10 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-6 bg-muted/40 p-6 md:p-8 rounded-xl">
          <p className="text-base sm:text-lg font-medium text-foreground text-center sm:text-left font-default">
            Need emergency restoration services?
          </p>
          <Button asChild size="lg" className="w-full sm:w-auto font-medium">
            <Link to="/ContactRequestServicePage">
              Request Emergency Service
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}